/**
 * ModelPlatformConfig.js
 *
 * Legacy compatibility shim. The real positioning system now lives in
 * SceneFraming.js + CharacterPoseConfig.js. This module exists so any
 * remaining callers of the old API (getPlatformConfig, updatePlatformConfig,
 * calculateOptimalCameraPosition, getHeightCategories, getModelsByHeight,
 * modelPlatformConfig) keep working without code changes elsewhere.
 *
 * Old fields (platformHeight, modelYOffset, heightCategory) are derived
 * on-the-fly from the new computePose() output.
 */

import { SCENE_FRAMING } from "./SceneFraming.js";
import {
    CHARACTER_POSES,
    computePose,
    DEFAULT_EYE_HEIGHT,
    listCharacterIds,
    saveOverride,
} from "./CharacterPoseConfig.js";

function deriveHeightCategory(eyeHeight) {
    if (!Number.isFinite(eyeHeight)) return "average";
    if (eyeHeight >= DEFAULT_EYE_HEIGHT + 15) return "very_tall";
    if (eyeHeight >= DEFAULT_EYE_HEIGHT + 5) return "taller";
    if (eyeHeight <= DEFAULT_EYE_HEIGHT - 5) return "shorter";
    return "average";
}

/**
 * Back-compat: derived map keyed by character ID.
 * Read-only-ish — mutating values here won't propagate.
 */
export const modelPlatformConfig = new Proxy({}, {
    get(_t, key) {
        if (typeof key !== "string") return undefined;
        if (!CHARACTER_POSES[key]) return undefined;
        return getPlatformConfig(key);
    },
    has(_t, key) {
        return typeof key === "string" && key in CHARACTER_POSES;
    },
    ownKeys() {
        return listCharacterIds();
    },
    getOwnPropertyDescriptor(_t, key) {
        if (typeof key === "string" && key in CHARACTER_POSES) {
            return {
                enumerable: true,
                configurable: true,
                value: getPlatformConfig(key),
            };
        }
        return undefined;
    },
});

export function getPlatformConfig(modelId) {
    const pose = computePose(modelId);
    return {
        name: pose.name || "Unknown Model",
        platformHeight: pose.platformY,
        modelYOffset: pose.modelY,
        platformScale: pose.platformScale,
        platformZ: pose.platformZ,
        heightCategory: deriveHeightCategory(pose.eyeHeight),
        eyeHeight: pose.eyeHeight,
        source: pose.source,
    };
}

export function getHeightCategories() {
    return ["very_tall", "taller", "average", "shorter"];
}

export function getModelsByHeight(category) {
    return listCharacterIds().filter((id) => {
        const cfg = getPlatformConfig(id);
        return cfg.heightCategory === category;
    });
}

/**
 * Map legacy { platformHeight, modelYOffset, ... } updates onto the new
 * pose model. We translate modelYOffset into eyeHeight so the result
 * positions the same way under computePose().
 */
export function updatePlatformConfig(modelId, newConfig) {
    if (!modelId || !newConfig || typeof newConfig !== "object") return;
    if (!CHARACTER_POSES[modelId]) {
        console.warn(
            `[ModelPlatformConfig shim] unknown character ID: ${modelId}`,
        );
        return;
    }
    const patch = {};
    const pose = computePose(modelId);
    if (Number.isFinite(newConfig.modelYOffset)) {
        // modelY = effectiveAnchorY - eyeHeight + faceTrim
        // => eyeHeight = effectiveAnchorY + faceTrim - modelY
        const eyeHeight =
            pose.effectiveAnchorY + pose.faceTrim - newConfig.modelYOffset;
        if (Number.isFinite(eyeHeight) && eyeHeight > 0) {
            patch.eyeHeight = eyeHeight;
            patch.detectedEyeHeight = eyeHeight;
        }
    }
    if (Number.isFinite(newConfig.platformHeight)) {
        // Legacy callers wrote platformHeight directly. Translate via:
        //   platformY = modelY - platformVerticalOffset
        //   modelY    = platformY + platformVerticalOffset
        // and then derive eyeHeight from the resulting modelY (using
        // the just-patched faceTrim if any modelYOffset was also sent).
        const targetModelY =
            newConfig.platformHeight + SCENE_FRAMING.platformVerticalOffset;
        const eyeHeight =
            pose.effectiveAnchorY + pose.faceTrim - targetModelY;
        if (Number.isFinite(eyeHeight) && eyeHeight > 0 && !("eyeHeight" in patch)) {
            patch.eyeHeight = eyeHeight;
            patch.detectedEyeHeight = eyeHeight;
        }
    }
    if (Number.isFinite(newConfig.platformScale)) {
        patch.scale = newConfig.platformScale;
    }
    if (Object.keys(patch).length > 0) {
        saveOverride(modelId, patch);
    }
}

export function calculateOptimalCameraPosition(_config) {
    return {
        position: { ...SCENE_FRAMING.cameraPosition },
        target: { ...SCENE_FRAMING.cameraTarget },
    };
}
